import { useQuery } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import {
  LEAD_INGEST_ADDRESS_QUERY_KEY,
  REVIEW_ITEMS_PENDING_QUERY_KEY,
  type ReviewQueueResponse
} from "shared/app/LeadsTableView/slices/ReviewQueueTable/types";

export const fetchPendingReviewItems = (page = 1, search = "") => {
  const params = new URLSearchParams({ status: "pending", page: String(page) });
  if (search.trim()) params.set("search", search.trim());

  return instance
    .get<ReviewQueueResponse>(
      `/portal/v1/boom_crm/lead_ingest_review_items?${params.toString()}`
    )
    .then((res) => res.data);
};

interface LeadIngestAddressResponse {
  address: string;
  catchall_enabled: boolean;
}

// Fetches the pending-review count and the ingest address for the Leads header.
// The items query shares REVIEW_ITEMS_PENDING_QUERY_KEY with ReviewQueueTable,
// so react-query dedupes the request and the cache stays in sync — dismissing
// or converting an item updates the counter without a second fetch.
export const useReviewQueueSummary = ({ enabled }: { enabled: boolean }) => {
  const { data: addressData } = useQuery({
    queryKey: LEAD_INGEST_ADDRESS_QUERY_KEY,
    queryFn: () =>
      instance
        .get<LeadIngestAddressResponse>(
          "/portal/v1/boom_crm/lead_ingest_address"
        )
        .then((res) => res.data),
    enabled
  });

  const catchallEnabled = addressData?.catchall_enabled ?? false;

  // Only fetch the pending count once the feature is actually on for the partner.
  const { data: reviewData } = useQuery({
    queryKey: REVIEW_ITEMS_PENDING_QUERY_KEY,
    queryFn: () => fetchPendingReviewItems(),
    enabled: enabled && catchallEnabled
  });

  return {
    pendingCount: reviewData?.total_count ?? 0,
    ingestAddress: addressData?.address,
    catchallEnabled
  };
};
